// frontend/src/pages/purchase/Suppliers.jsx

import React, { useState, useEffect } from 'react';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../../utils/supplierApi';
import InputMask from '@mona-health/react-input-mask';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  // 現在「編集モード」かどうかを判定するためのState
  const [editingUuid, setEditingUuid] = useState(null);

  const [formData, setFormData] = useState({
    supplier_code: '', // 🌟 追加：仕入先コード
    name: '',
    contact_name: '',
    phone: '',
    email: '',
    zipcode: '',
    address: '',
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error(error);
      alert('データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // フォームのリセット処理
  const resetForm = () => {
    setEditingUuid(null);
    setFormData({ supplier_code: '', name: '', contact_name: '', phone: '', email: '', zipcode: '', address: '', note: '' });
  };

  // 送信処理（新規登録 or 更新）
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUuid) {
        await updateSupplier(editingUuid, formData);
        alert('仕入先情報を更新しました！');
      } else {
        await createSupplier(formData);
        alert('仕入先を新規登録しました！');
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error(error);
      alert(editingUuid ? '更新に失敗しました。コードが重複している可能性があります。' : '登録に失敗しました。コードが重複している可能性があります。');
    }
  };

  // 「編集」ボタンを押したときの処理
  const handleEditClick = (supplier) => {
    setEditingUuid(supplier.uuid);
    setFormData({
      supplier_code: supplier.supplier_code || '', // 🌟 編集時にセット
      name: supplier.name || '',
      contact_name: supplier.contact_name || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      zipcode: supplier.zipcode || '',
      address: supplier.address || '',
      note: supplier.note || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 「削除」ボタンを押したときの処理
  const handleDeleteClick = async (uuid) => {
    if (!window.confirm('本当にこの仕入先を削除してよろしいですか？（この操作は取り消せません）')) {
      return;
    }
    try {
      await deleteSupplier(uuid);
      alert('仕入先を削除しました。');
      if (editingUuid === uuid) resetForm();
      fetchData();
    } catch (error) {
      console.error(error);
      alert('削除に失敗しました。');
    }
  };

  return (
    <div>
      <h1 className="page-header">仕入先管理 <small>Suppliers</small></h1>

      <div className="row">
        {/* === 入力フォーム === */}
        <div className="col-xl-4">
          <div className={`panel ${editingUuid ? 'panel-warning' : 'panel-inverse'}`}>
            <div className="panel-heading">
              <h4 className="panel-title">{editingUuid ? '仕入先情報の編集' : '新規仕入先の登録'}</h4>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit}>

                {/* 🌟 仕入先コード入力欄（2桁数字） */}
                <div className="form-group mb-3">
                  <label className="form-label text-primary fw-bold">仕入先コード (2桁) <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-lg fw-bold"
                    name="supplier_code"
                    value={formData.supplier_code}
                    onChange={handleInputChange}
                    placeholder="例: 01"
                    maxLength="2"
                    pattern="[0-9]{2}"
                    title="00から99までの2桁の数字を入力してください"
                    required
                  />
                  <div className="form-text">システム内で管理する2桁の数字（00〜99）</div>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-bold">仕入先名 <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required />
                </div>
                <div className="form-group mb-3">
                  <label className="form-label">担当者名</label>
                  <input type="text" className="form-control" name="contact_name" value={formData.contact_name} onChange={handleInputChange} />
                </div>

                <div className="row mb-3">
                  <div className="col-md-5">
                    <label className="form-label">郵便番号</label>
                    <InputMask
                      mask="999-9999"
                      maskChar={null}
                      className="form-control"
                      name="zipcode"
                      placeholder="例: 100-0001"
                      value={formData.zipcode}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-7">
                    <label className="form-label">電話番号</label>
                    <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-group mb-3">
                  <label className="form-label">住所</label>
                  <input type="text" className="form-control" name="address" placeholder="住所" value={formData.address} onChange={handleInputChange} />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">メールアドレス</label>
                  <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} />
                </div>
                <div className="form-group mb-4">
                  <label className="form-label">備考メモ</label>
                  <textarea className="form-control" name="note" rows="3" value={formData.note} onChange={handleInputChange}></textarea>
                </div>

                <div className="d-flex gap-2">
                  <button type="submit" className={`btn w-100 fw-bold ${editingUuid ? 'btn-warning' : 'btn-primary'}`}>
                    {editingUuid ? '更新する' : '登録する'}
                  </button>
                  {editingUuid && (
                    <button type="button" className="btn btn-default w-100" onClick={resetForm}>
                      キャンセル
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* === 仕入先一覧テーブル === */}
        <div className="col-xl-8">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">仕入先一覧</h4>
            </div>
            <div className="panel-body">
              {loading ? (
                <p>読み込み中...</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered align-middle">
                    <thead>
                      <tr>
                        <th width="8%" className="text-center">コード</th>
                        <th width="20%">仕入先名</th>
                        <th width="15%">担当者</th>
                        <th width="30%">住所・連絡先</th>
                        <th width="12%">登録日</th>
                        <th width="15%" className="text-center">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">登録されている仕入先がありません。</td>
                        </tr>
                      ) : (
                        suppliers.map((supplier) => (
                          <tr key={supplier.uuid}>
                            {/* 🌟 コードをバッジとして表示 */}
                            <td className="text-center">
                              <span className="badge bg-secondary fs-6 px-2 py-1">{supplier.supplier_code || '--'}</span>
                            </td>
                            <td className="fw-bold text-primary">{supplier.name}</td>
                            <td>{supplier.contact_name || '-'}</td>
                            <td>
                              {(supplier.zipcode || supplier.address) && (
                                <div className="mb-1 text-muted small">
                                  {supplier.zipcode && <span>〒{supplier.zipcode} </span>}
                                  {supplier.address}
                                </div>
                              )}
                              {supplier.phone && <div><i className="fa fa-phone me-1 text-muted"></i>{supplier.phone}</div>}
                              {supplier.email && <div><i className="fa fa-envelope me-1 text-muted"></i>{supplier.email}</div>}
                            </td>
                            <td>{new Date(supplier.created_at).toLocaleDateString()}</td>
                            <td className="text-center">
                              <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEditClick(supplier)}>
                                <i className="fa fa-edit"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(supplier.uuid)}>
                                <i className="fa fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Suppliers;